import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import useI18n from '@/mixins/useI18n';
import useUser from '@/mixins/useUser';
import useBCXConfirm from '@/mixins/useBCXConfirm';
import useMessengerState from '@/state/messenger/messengerState';
import sanitizeTitleInput from '@/utils/sanitizeTitleInput';
import { getDisplayName } from '@/utils/user';
import { ChatGroupMember, GROUP_NAME_MAX_LENGTH, GROUP_NAME_MIN_LENGTH } from '@/models/Messenger';
import { BaseUserWithRole, User } from '@/models/User';
import MessengerGroupService from '@/services/MessengerGroupService';
import userFromState from '@/state/messenger/userFromState';
import BCXTextArea from '@/components/molecules/forms/BCXTextArea.vue';
import BCXMessengerUserSelect from '@/components/messenger/User/BCXMessengerUserSelect.vue';
import BCXMessengerChatGroupMember from '@/components/messenger/ChatGroup/BCXMessengerChatGroupMember.vue';
import BCXMessengerConfirm from '@/components/messenger/BCXMessengerConfirm.vue';
import MarvinInfoBox from '@/views/recommendations/components/MarvinInfoBox.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXMessengerChatGroupPanel',
  setup(__props) {

const {
  openChatgroupPanel, closeChatgroupPanel, chatgroupGroupId, chatgroups, loadChatgroups, selectedChatId
} = useMessengerState();
const { t } = useI18n();
const { user: me } = useUser('self');

const groupId = ref<string | null>(chatgroupGroupId.value);
const name = ref<string>(t('messenger.chatgroup.name_default').toString());
const persistedName = ref<string>('');
const creatorId = ref<string>('');
const members = ref<ChatGroupMember[]>([]); // all members incl. the admin (or picks while creating)
const loaded = ref<boolean>(false);

const isExisting = computed(() => chatgroupGroupId.value !== null);
const isMemberView = computed(() => isExisting.value && !!creatorId.value && creatorId.value !== me.value?.userId);

// The admin is the group creator; while creating (no group yet) it's the current user.
const adminMember = computed<ChatGroupMember | null>(() => {
  const found = members.value.find((m) => m.user === creatorId.value);
  if (found) return found;
  if (groupId.value === null && me.value?.userId) {
    return { user: me.value.userId, state: 'ACTIVE', joinedAt: '' };
  }
  return null;
});
// Members invited while this panel is open stay on top, so the invite is visible.
const justInvitedIds = ref<string[]>([]);

const inviteMessage = ref<string>('');
const isInviteMessageEnlarged = ref<boolean>(false);
// While the group is still unsaved the invites go out on save — keep their messages until then.
const pendingInviteMessages = ref<Record<string, string>>({});

const memberName = (member: ChatGroupMember) => getDisplayName(userFromState(member.user) as BaseUserWithRole);

// justInvitedIds is newest-first, so its index doubles as the sort rank.
const invitedRank = (member: ChatGroupMember) => {
  const index = justInvitedIds.value.indexOf(member.user);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const otherMembers = computed<ChatGroupMember[]>(() => members.value
  .filter((m) => m.user !== adminMember.value?.user)
  .slice()
  .sort((a, b) => {
    const byInvite = invitedRank(a) - invitedRank(b);
    if (byInvite !== 0) return byInvite;
    return memberName(a).localeCompare(memberName(b));
  }));

// Users already in the group (admin + members) — hidden from the invite search.
const excludeIds = computed<string[]>(() => [adminMember.value?.user, ...members.value.map((m) => m.user)]
  .filter((id): id is string => !!id));

const reloadMembers = async () => {
  if (!groupId.value) return;
  // Backend returns only current members (active + pending), never INACTIVE.
  members.value = await MessengerGroupService.getMembers(groupId.value);
};

onMounted(async () => {
  if (chatgroupGroupId.value) {
    const group = chatgroups.value.find((g) => g.groupId === chatgroupGroupId.value);
    if (group) {
      groupId.value = group.groupId;
      creatorId.value = group.creator;
      name.value = group.title;
      persistedName.value = group.title;
      await reloadMembers();
    }
  }
  loaded.value = true;
});

const onNameInput = () => {
  name.value = sanitizeTitleInput(name.value).slice(0, GROUP_NAME_MAX_LENGTH);
};

const charsLeft = computed(() => Math.max(0, GROUP_NAME_MAX_LENGTH - name.value.length));
const isValid = computed(() => name.value.trim().length >= GROUP_NAME_MIN_LENGTH);

const showSaveButtons = computed(() => groupId.value === null || name.value !== persistedName.value);

const cancel = () => {
  if (groupId.value === null) {
    closeChatgroupPanel();
  } else {
    name.value = persistedName.value;
  }
};

const save = async () => {
  if (!isValid.value) return;
  if (groupId.value === null) {
    const group = await MessengerGroupService.createGroup(name.value.trim());
    // Backend creates the group empty (creator only) — invite the picked members.
    await Promise.all(otherMembers.value.map(
      (m) => MessengerGroupService.addMember(group.groupId, m.user, pendingInviteMessages.value[m.user])
    ));
    await loadChatgroups();
    openChatgroupPanel(group.groupId);
    return;
  }
  const group = await MessengerGroupService.renameGroup(groupId.value, name.value.trim());
  persistedName.value = group?.title ?? name.value.trim();
  await loadChatgroups();
};

const inviteMember = async (user: User, personalMessage: string) => {
  if (!user.userId || user.userId === me.value?.userId) return;
  if (members.value.some((m) => m.user === user.userId)) return;
  justInvitedIds.value = [user.userId, ...justInvitedIds.value];
  if (groupId.value) {
    await MessengerGroupService.addMember(groupId.value, user.userId, personalMessage);
    await reloadMembers();
  } else {
    pendingInviteMessages.value = { ...pendingInviteMessages.value, [user.userId]: personalMessage };
    members.value = [{ user: user.userId, state: 'INVITED', joinedAt: '' }, ...members.value];
  }
};

const removeMember = async (member: ChatGroupMember) => {
  if (groupId.value) {
    await MessengerGroupService.removeMember(groupId.value, member.user);
    await reloadMembers();
  } else {
    const messages = { ...pendingInviteMessages.value };
    delete messages[member.user];
    pendingInviteMessages.value = messages;
    members.value = members.value.filter((m) => m.user !== member.user);
  }
};

const pendingName = ref<string>('');
const { showConfirm: showInviteConfirm, ask: askInvite, onConfirm: onConfirmInvite } = useBCXConfirm();
const { showConfirm: showRemoveConfirm, ask: askRemove, onConfirm: onConfirmRemove } = useBCXConfirm();
const { showConfirm: showLeaveConfirm, ask: askLeave, onConfirm: onConfirmLeave } = useBCXConfirm();
const { showConfirm: showDeleteConfirm, ask: askDelete, onConfirm: onConfirmDelete } = useBCXConfirm();
const {
  showConfirm: showDeleteMembersConfirm, ask: askDeleteMembers, onConfirm: onConfirmDeleteMembers
} = useBCXConfirm();

const onInvite = async (user: User) => {
  pendingName.value = getDisplayName(user as BaseUserWithRole);
  inviteMessage.value = '';
  isInviteMessageEnlarged.value = false;
  if (await askInvite()) await inviteMember(user, inviteMessage.value);
};

const onRemove = async (member: ChatGroupMember) => {
  pendingName.value = getDisplayName(userFromState(member.user) as BaseUserWithRole);
  if (await askRemove()) await removeMember(member);
};

const onLeave = async () => {
  if (await askLeave()) {
    if (groupId.value) await MessengerGroupService.leaveGroup(groupId.value);
    closeChatgroupPanel();
  }
};

// Only the group owner can delete, and only once the group exists.
const canDelete = computed(() => !!groupId.value && !isMemberView.value);

const deleteConfirmWord = ref<string>('');
const isDeleteConfirmed = computed(
  () => deleteConfirmWord.value === t('messenger.chatgroup.confirm.delete_confirm_word').toString()
);

const onDelete = async () => {
  deleteConfirmWord.value = '';
  const confirmed = otherMembers.value.length ? await askDeleteMembers() : await askDelete();
  if (!confirmed || !groupId.value) return;
  await MessengerGroupService.deleteGroup(groupId.value);
  // The deleted group must not stay selected, closing reloads its messages otherwise.
  if (selectedChatId.value === groupId.value) selectedChatId.value = '';
  closeChatgroupPanel();
};

return { __sfc: true,openChatgroupPanel, closeChatgroupPanel, chatgroupGroupId, chatgroups, loadChatgroups, selectedChatId, t, me, groupId, name, persistedName, creatorId, members, loaded, isExisting, isMemberView, adminMember, justInvitedIds, inviteMessage, isInviteMessageEnlarged, pendingInviteMessages, memberName, invitedRank, otherMembers, excludeIds, reloadMembers, onNameInput, charsLeft, isValid, showSaveButtons, cancel, save, inviteMember, removeMember, pendingName, showInviteConfirm, askInvite, onConfirmInvite, showRemoveConfirm, askRemove, onConfirmRemove, showLeaveConfirm, askLeave, onConfirmLeave, showDeleteConfirm, askDelete, onConfirmDelete, showDeleteMembersConfirm, askDeleteMembers, onConfirmDeleteMembers, onInvite, onRemove, onLeave, canDelete, deleteConfirmWord, isDeleteConfirmed, onDelete, GROUP_NAME_MAX_LENGTH, BCXTextArea, BCXMessengerUserSelect, BCXMessengerChatGroupMember, BCXMessengerConfirm, MarvinInfoBox }
}

})